import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Award, Star, Trophy, ArrowRight, Camera, Sparkles } from 'lucide-react';
import { WINNERS, UNNAMED_WINNERS } from '../data';
import { Winner } from '../types';
import Modal from '../components/Modal';
import SafeImage from '../components/SafeImage';
import Seo from '../components/Seo';
import { PAGE_METADATA, getFacesStructuredData } from '../seo/pageMetadata';

const MissNigeriaEntrepreneurs = () => {
  const [selectedWinner, setSelectedWinner] = useState<Winner | null>(null);
  const getWinnerAltText = (winner: Winner, index: number) =>
    winner.name
      ? `Portrait of Miss ${winner.name}, ${winner.title} winner in ${winner.year}`
      : `Portrait of a Delight Business Empowerment recipient ${index + 1}`;

  return (
    <>
      <Seo {...PAGE_METADATA.faces} structuredData={getFacesStructuredData()} />
      <div className="pt-32 pb-24 overflow-hidden">
      <section className="px-6 max-w-7xl mx-auto text-center mb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-100 px-4 py-2 rounded-full mb-6">
            <Award className="w-4 h-4 text-royal-gold fill-royal-gold" />
            <span className="text-royal-gold text-[10px] font-black uppercase tracking-[0.2em]">Hall of Fame</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-display font-black text-royal-dark tracking-tighter mb-6 break-words">
            Delight <span className="text-royal-purple">Business Empowerment</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto font-medium">
            Celebrating the grace, intelligence, and impact of our past winners. These women represent the pinnacle of beauty and empowerment.
          </p>
        </motion.div>
      </section>

      <section className="px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {WINNERS.map((winner, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => setSelectedWinner(winner)}
              className="group bg-white rounded-[3rem] overflow-hidden shadow-2xl shadow-black/5 border border-slate-100 cursor-pointer"
            >
              <div className="h-[450px] overflow-hidden relative">
                <SafeImage 
                  src={winner.img} 
                  alt={getWinnerAltText(winner, idx)}
                  width={1200}
                  height={1500}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-royal-dark/90 via-royal-dark/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                  <div className="bg-white/20 backdrop-blur-md p-4 rounded-full text-white scale-50 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-500">
                    <Camera className="w-8 h-8" />
                  </div>
                </div>
                
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur px-5 py-2.5 rounded-2xl font-display font-black text-royal-purple text-sm shadow-xl">
                  {winner.prize}
                </div>

                <div className="absolute bottom-8 left-8 right-8 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="flex items-center gap-2 text-royal-gold mb-2">
                    <Star className="w-4 h-4 fill-royal-gold" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Alumni {winner.year}</span>
                  </div>
                  {winner.name && <h4 className="text-white text-2xl font-display font-black mb-2">Miss {winner.name}</h4>}
                  <p className="text-white/70 text-sm font-medium leading-relaxed">
                    {winner.story || "A visionary leader scaling impact through innovation and dedication."}
                  </p>
                </div>
              </div>
              <div className="p-10">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="text-royal-gold font-black text-[10px] uppercase tracking-widest">{winner.title}</span>
                    {winner.name && <h4 className="text-2xl font-display font-black text-royal-dark mt-1">Miss {winner.name}</h4>}
                  </div>
                  <div className="bg-slate-50 p-3 rounded-2xl">
                    <Trophy className="w-5 h-5 text-royal-gold" />
                  </div>
                </div>
                <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-royal-purple font-black uppercase tracking-widest">Gallery</p>
                    <p className="text-sm font-bold text-royal-dark">View More</p>
                  </div>
                  <div className="text-royal-purple p-2 bg-royal-accent rounded-xl">
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Unnamed Winners Section */}
      <section className="px-6 max-w-7xl mx-auto mt-32">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-display font-black text-royal-dark tracking-tighter">
            Empowerment <span className="text-royal-purple">Gallery</span>
          </h2>
          <p className="text-slate-500 font-medium mt-4">Celebrating the journey of every participant in our empowerment movement.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {UNNAMED_WINNERS.map((winner, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => setSelectedWinner(winner)}
              className="group bg-white rounded-[3rem] overflow-hidden shadow-2xl shadow-black/5 border border-slate-100 cursor-pointer"
            >
              <div className="h-[450px] overflow-hidden relative">
                <SafeImage
                  src={winner.img}
                  alt={getWinnerAltText(winner, idx)}
                  width={1200}
                  height={1500}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                {winner.hideDetails ? (
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-3 py-1 rounded-xl font-display font-black text-royal-purple text-xs shadow-xl">
                    WINNER
                  </div>
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-royal-dark/90 via-royal-dark/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                      <div className="bg-white/20 backdrop-blur-md p-4 rounded-full text-white scale-50 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-500">
                        <Camera className="w-8 h-8" />
                      </div>
                    </div>

                    <div className="absolute top-6 right-6 bg-white/95 backdrop-blur px-5 py-2.5 rounded-2xl font-display font-black text-royal-purple text-sm shadow-xl">
                      {winner.prize}
                    </div>

                    <div className="absolute bottom-8 left-8 right-8 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                      <div className="flex items-center gap-2 text-royal-gold mb-2">
                        <Star className="w-4 h-4 fill-royal-gold" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Alumni {winner.year}</span>
                      </div>
                      <p className="text-white/70 text-sm font-medium leading-relaxed">
                        {winner.story}
                      </p>
                    </div>
                  </>
                )}
              </div>
              {!winner.hideDetails && (
                <div className="p-10">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-royal-gold font-black text-[10px] uppercase tracking-widest">{winner.title}</span>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-2xl">
                      <Trophy className="w-5 h-5 text-royal-gold" />
                    </div>
                  </div>
                  <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-royal-purple font-black uppercase tracking-widest">Gallery</p>
                      <p className="text-sm font-bold text-royal-dark">View More</p>
                    </div>
                    <div className="text-royal-purple p-2 bg-royal-accent rounded-xl">
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery Modal */}
      <Modal
        isOpen={!!selectedWinner}
        onClose={() => setSelectedWinner(null)}
        title={selectedWinner?.name ? `Miss ${selectedWinner.name}` : "Empowerment Journey"}
      >
        <div className="space-y-8">
          {/* Winner Info Header */}
          <div className="flex flex-wrap items-center gap-4 p-6 bg-royal-accent/30 rounded-[2rem] border border-amber-200">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <Trophy className="w-4 h-4 text-royal-gold" />
              <span className="text-royal-dark font-black text-xs uppercase tracking-widest">{selectedWinner?.title}</span>
            </div>
            {selectedWinner?.prize && (
              <div className="flex items-center gap-2 bg-royal-dark text-white px-4 py-2 rounded-full shadow-sm">
                <Sparkles className="w-4 h-4" />
                <span className="font-black text-xs uppercase tracking-widest">{selectedWinner?.prize} Grant</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {selectedWinner?.gallery?.map((img, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                className="aspect-[4/5] rounded-3xl overflow-hidden shadow-lg"
              >
                <SafeImage 
                  src={img} 
                  alt={
                    selectedWinner?.name
                      ? `Gallery portrait of Miss ${selectedWinner.name} ${idx + 1}`
                      : `Gallery portrait of a Delight Business Empowerment recipient ${idx + 1}`
                  }
                  width={1200}
                  height={1500}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
          <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
            <h4 className="text-xl font-display font-black text-royal-dark mb-4">About the Journey</h4>
            <p className="text-slate-600 leading-relaxed font-medium">
              {selectedWinner?.story || "This journey represents the dedication and hard work put into creating a lasting impact that inspires the community."}
            </p>
          </div>
        </div>
      </Modal>

      {/* Call to Action */}
      <section className="mt-32 px-6 max-w-5xl mx-auto">
        <div className="bg-royal-dark rounded-[4rem] p-16 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-royal-purple/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <h2 className="text-4xl font-display font-black mb-6">Could You Be Next?</h2>
          <p className="text-royal-accent/60 mb-10 text-lg font-medium">We are looking for the next Delight Business Empowerment ambassador. Applications for the 2025 cohort are now open.</p>
          <Link 
            to="/register"
            className="inline-block bg-white text-royal-dark px-10 py-5 rounded-2xl font-black text-lg shadow-xl hover:scale-105 transition-all"
          >
            Apply for 2025 Contest
          </Link>
        </div>
      </section>
      </div>
    </>
  );
};

export default MissNigeriaEntrepreneurs;
